import React, { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";

/**
 * OCI V2 — Compact Gift Aid KPI strip.
 * Drop into existing tabs without disrupting layout. Strictly additive.
 *
 * Props:
 *   variant: 'organisation' | 'fundraiser' (default 'organisation')
 *   fundraiserId: required if variant='fundraiser'
 */
const formatGBP = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
const formatPct = (v) => `${(v || 0).toFixed(1)}%`;
const formatInt = (v) => new Intl.NumberFormat("en-GB").format(v || 0);

const Tile = ({ label, value, tone, sub, testid }) => {
  const toneClass = {
    positive: "text-emerald-700",
    danger: "text-rose-700",
    warning: "text-amber-700",
    accent: "text-indigo-700",
  }[tone] || "text-slate-900";
  return (
    <div className="rounded-lg border bg-white px-3 py-2.5 min-w-[140px]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
};

export const GiftAidMetricsStrip = ({ variant = "organisation", fundraiserId = null, className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (variant === "fundraiser") {
          const params = fundraiserId ? { fundraiserId } : {};
          const r = await apiClient.get("/gift-aid/fundraiser-performance", { params });
          const f = (r.data?.fundraisers || []).find((x) => !fundraiserId || x.fundraiserId === fundraiserId)
                    || (r.data?.fundraisers || [])[0]
                    || null;
          setData(f);
        } else {
          const r = await apiClient.get("/gift-aid/dashboard");
          setData(r.data);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load Gift Aid metrics");
      } finally {
        setLoading(false);
      }
    })();
  }, [variant, fundraiserId]);

  if (loading) {
    return <div className={`text-xs text-slate-400 ${className}`} data-testid="ga-strip-loading">Loading Gift Aid…</div>;
  }
  if (error || !data) return null;  // Silent — additive, never breaks parent

  if (variant === "fundraiser") {
    return (
      <Card className={`border-indigo-100 ${className}`} data-testid="ga-fundraiser-strip">
        <CardContent className="p-3 flex flex-wrap gap-2">
          <Tile label="GA Capture %" value={formatPct(data.giftAidCapturePct)} tone="accent" testid="ga-fr-capture-pct" />
          <Tile label="GA Captured" value={formatInt(data.giftAidCapturedCount)} sub={`of ${formatInt(data.giftAidEligibleCount)} eligible`} testid="ga-fr-captured" />
          <Tile label="Value Generated" value={formatGBP(data.giftAidValueGenerated)} tone="positive" testid="ga-fr-generated" />
          <Tile label="Value Lost" value={formatGBP(data.giftAidValueLost)} tone="danger" testid="ga-fr-lost" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-emerald-100 ${className}`} data-testid="ga-org-strip">
      <CardContent className="p-3 flex flex-wrap gap-2">
        <Tile label="Recoverable" value={formatGBP(data.giftAidRecoverable)} tone="positive" testid="ga-org-recoverable" />
        <Tile label="Submitted" value={formatGBP(data.giftAidSubmitted)} testid="ga-org-submitted" />
        <Tile label="Paid" value={formatGBP(data.giftAidPaid)} tone="positive" testid="ga-org-paid" />
        <Tile label="Outstanding" value={formatGBP(data.giftAidOutstanding)} tone="warning" testid="ga-org-outstanding" />
        <Tile label="Opportunity Lost" value={formatGBP(data.giftAidOpportunityLost)} tone="danger" sub="No declaration" testid="ga-org-opportunity-lost" />
        <Tile label="Eligible %" value={formatPct(data.giftAidEligiblePct)} tone="accent" testid="ga-org-eligible-pct" />
      </CardContent>
    </Card>
  );
};

export default GiftAidMetricsStrip;
